"use client";

import Link, { type LinkProps } from "next/link";
import type { ComponentPropsWithoutRef } from "react";
import { getBridge } from "@/src/lib/bridge/bridge";
import {
  isInNativeDetailShell,
  isOffSiteNavigationHref,
  navigateShellAware,
  openInAppBrowserUrl,
  resolveAppNavigationUrl,
  shouldOpenInAppBrowserForHref,
  shouldPopDetailForCrossTabShellNavigation,
  shouldPopDetailForTabShellNavigation,
  shouldUseNativeDetailForHref,
  openNativeDetailForHref,
} from "@/src/lib/bridge/shellAppNavigation";
import { useRouter } from "next/navigation";

export type ShellAwareLinkProps = LinkProps &
  Omit<ComponentPropsWithoutRef<"a">, keyof LinkProps>;

/** Next.js `Link` 专有属性，站外 `<a>` 分支需剥离 */
type NextLinkOnlyProps = {
  prefetch?: LinkProps["prefetch"];
  replace?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  passHref?: boolean;
  locale?: string | false;
};

function linkHrefToString(href: ShellAwareLinkProps["href"]): string {
  if (typeof href === "string") {
    return href;
  }
  if (href && typeof href === "object" && "pathname" in href) {
    const o = href as { pathname?: string; search?: string };
    const p = o.pathname ?? "/";
    const s = o.search;
    return typeof s === "string" && s.length > 0 ? `${p}${s}` : p;
  }
  return "/";
}

function stripNextLinkOnlyProps(
  rest: Omit<ShellAwareLinkProps, "href" | "onClick">,
): Omit<ComponentPropsWithoutRef<"a">, "href" | "onClick"> {
  const {
    prefetch: _prefetch,
    replace: _replace,
    scroll: _scroll,
    shallow: _shallow,
    passHref: _passHref,
    locale: _locale,
    ...anchorProps
  } = rest as ShellAwareLinkProps & NextLinkOnlyProps;
  return anchorProps;
}

/**
 * 壳感知 Link（`ShellLink` / `ShellAwareLink` 别名），点击分流与 `navigateShellAware` 一致。
 *
 * **渲染**
 * - 站外（非 Edgen 域 `https?`、mailto、tel）→ `<a>`，http(s) 默认 `target="_blank"`
 * - 其余 → Next `Link`
 *
 * **点击（仅 App 内拦截；浏览器走 Link / `<a>` 默认）**
 * | 链接类型 | 行为 |
 * |----------|------|
 * | 站外 / mailto / tel | `open-url-inapp`（内置浏览器） |
 * | Edgen 绝对链且非四 Tab 壳（如 beta 域 `/blog/...`） | `open-url-inapp` |
 * | Detail → 四 Tab 壳路径 | `popDetailAndNavigateShell` |
 * | 当前 Tab → 另一 Tab 的壳路径 | `popDetailAndNavigateShell` |
 * | Tab 壳 → 非壳路径（且不在 Detail） | `openDetailInNative` |
 * | Detail 内非壳互跳 | `navigateShellAware`（维护 `__edgenDetailIdx`） |
 * | 本 Tab 壳内 SPA | 不拦截，走 `Link` / `router` |
 */
export function ShellLink({ href, onClick, ...rest }: ShellAwareLinkProps) {
  // 必须是**裸** `useRouter()`：本组件自己就做了壳分流，`navigateShellAware` 只负责最后一跳。
  // 传 `useShellRouter()` 会让 `router.push` 二次进入 `navigateShellAware` —— 同一次点击里
  // 外层已占用二级容器导航窗口，内层随即被判成「重复导航」丢弃，表现为**点了完全没反应**。
  const router = useRouter();
  const resolvedHref = linkHrefToString(href);
  const offSite = isOffSiteNavigationHref(resolvedHref);
  const inApp = getBridge().isInApp();
  const linkReplace = (rest as NextLinkOnlyProps).replace === true;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) {
      return;
    }

    // Web 环境不走壳分流逻辑，保持 Link / <a> 默认行为
    if (!inApp) {
      return;
    }

    // 站外 / mailto / tel：App 内走 open-url-inapp
    if (offSite) {
      e.preventDefault();
      openInAppBrowserUrl(resolveAppNavigationUrl(resolvedHref));
      return;
    }

    // Edgen 绝对链（如 https://edgen.beta.edgen.tech/blog/...）：勿 open-detail / 勿 target=_blank 落系统浏览器
    if (shouldOpenInAppBrowserForHref(resolvedHref)) {
      e.preventDefault();
      openInAppBrowserUrl(resolveAppNavigationUrl(resolvedHref));
      return;
    }

    // Detail 内 → 壳路径：关 Detail + 对应 Tab 加载
    if (shouldPopDetailForTabShellNavigation(resolvedHref)) {
      e.preventDefault();
      getBridge().popDetailAndNavigateShell(
        resolveAppNavigationUrl(resolvedHref),
      );
      return;
    }

    // Tab 壳 → 另一 Tab 的壳路径：切 Tab，勿在当前 WebView 内 Link 跳转
    if (shouldPopDetailForCrossTabShellNavigation(resolvedHref)) {
      e.preventDefault();
      getBridge().popDetailAndNavigateShell(
        resolveAppNavigationUrl(resolvedHref),
      );
      return;
    }

    // Detail 内非壳互跳：须走 navigateShellAware，否则 Next Link 不递增 __edgenDetailIdx，返回会误关 Detail
    if (isInNativeDetailShell()) {
      e.preventDefault();
      navigateShellAware(router, resolvedHref, { replace: linkReplace });
      return;
    }

    // Tab 壳 → 非壳路径：打开二级 Detail
    if (shouldUseNativeDetailForHref(resolvedHref)) {
      e.preventDefault();
      openNativeDetailForHref(resolvedHref);
    }
  };

  if (!getBridge().isInApp()) {
    return <Link {...rest} href={href} onClick={onClick} />;
  }

  if (offSite) {
    const anchorProps = stripNextLinkOnlyProps(rest);
    const absoluteHref = resolveAppNavigationUrl(resolvedHref);
    const lower = resolvedHref.trim().toLowerCase();
    const isSpecialScheme =
      lower.startsWith("mailto:") || lower.startsWith("tel:");
    const target = rest.target ?? (isSpecialScheme ? undefined : "_blank");
    const rel =
      rest.rel ?? (target === "_blank" ? "noopener noreferrer" : undefined);

    return (
      <a
        {...anchorProps}
        href={absoluteHref}
        target={target}
        rel={rel}
        onClick={handleClick}
      />
    );
  }

  return <Link {...rest} href={href} onClick={handleClick} />;
}
